/**
 * Client for the ironSource refresh API.
 *
 * The server wraps every success in { success, message, data } via sendSuccess,
 * so payloads live under `data` rather than at the response root. Errors come
 * from the global errorMiddleware and are surfaced as ApiError.
 *
 * The ironSource bearer token is passed in the request BODY as auth_token, never
 * in the Authorization header — that header is reserved for the server's own JWT.
 */

/**
 * Full base URL for the ironSource routes, including the module path — e.g.
 * https://host/api/v1/ironsource. Keeping the module path in config rather than
 * hardcoding it here means remounting the router server-side needs no client change.
 *
 * Vite inlines this at BUILD time, so changing it requires a rebuild, not a restart.
 */
const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/+$/, '');

if (!API_BASE) {
  // Failing loudly beats silently issuing same-origin requests that 404 on the
  // static host and look like a server problem.
  throw new Error('VITE_API_BASE_URL is not set. Configure it and rebuild.');
}

export interface AppSummary {
  app_key: string;
  app_name: string;
}

export interface AdUnit {
  instance_id: number | string;
  instance_name: string;
  ad_format: string;
  previous_ad_unit_id: string;
  new_ad_unit_id: string;
  rate: number | string | null;
}

export interface AppAdUnits {
  app_key: string;
  app_name: string;
  ad_units: AdUnit[];
}

export interface InstanceStatus {
  instanceId: number | string;
  statusCode: number;
}

export interface AppUpdateResult {
  app_key: string;
  status: InstanceStatus[];
  success: boolean;
  message?: string;
}

export class ApiError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

interface Envelope<T> {
  success?: boolean;
  message?: string;
  data?: T;
}

async function post<T>(path: string, body: unknown): Promise<{ data: T; message: string; status: number }> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch {
    // fetch rejects only on network/CORS failure, never on an HTTP error status.
    throw new ApiError('Could not reach the server. Check your connection.', 0);
  }

  let envelope: Envelope<T> = {};
  try {
    envelope = (await response.json()) as Envelope<T>;
  } catch {
    // Fall through — an empty body on an error status is still an error.
  }

  if (!response.ok) {
    throw new ApiError(envelope.message ?? `Request failed (${response.status})`, response.status);
  }

  return {
    data: envelope.data as T,
    message: envelope.message ?? '',
    status: response.status,
  };
}

/** Authenticates against ironSource and lists active apps. */
export async function startScan(
  secretKey: string,
  refreshToken: string
): Promise<{ auth_token: string; apps: AppSummary[] }> {
  const { data } = await post<{ auth_token: string; apps: AppSummary[] }>('/scan/start', {
    secretKey,
    refreshToken,
  });
  return data;
}

/** Fetches the managed ad units for one app, with the next ID already derived. */
export async function scanApp(
  authToken: string,
  app: AppSummary
): Promise<AppAdUnits> {
  const { data } = await post<AppAdUnits>('/scan/app', {
    auth_token: authToken,
    app_key: app.app_key,
    app_name: app.app_name,
  });
  return data;
}

/**
 * Applies the new ad unit IDs.
 *
 * Returns HTTP 207 when some apps failed — note that `response.ok` is true for
 * 207, so callers must read `status` to detect a partial write rather than
 * assuming success.
 */
export async function updateAdUnits(
  authToken: string,
  apps: AppAdUnits[]
): Promise<{ results: AppUpdateResult[]; message: string; partial: boolean }> {
  const { data, message, status } = await post<AppUpdateResult[]>('/update', {
    auth_token: authToken,
    apps,
  });
  return { results: data ?? [], message, partial: status === 207 };
}
